/*
 * 0.90 (K3): Beteiligte ohne Konto – sichtbare Arbeitsbezüge, kein Adressbuch.
 *  - Board: „Beteiligte ohne Konto …“ im Board-Menü (Besitzer*innen schalten ein; Bearbeitende pflegen Personen)
 *  - Karte: Abschnitt „Beteiligte“ nur, wenn eingeschaltet – Rolle, Person (oder „Offen“) und kurzer Beitrag
 * Keine Konten, keine Rechte, keine Nachrichten, keine Einladungen. Nur mit Verbindung.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Contact, Pencil, Plus, Trash2, UserPlus, X } from 'lucide-react';
import {
  PARTICIPANT_NAME_MAX,
  PARTICIPANT_ORG_MAX,
  PARTICIPANTS_VISIBILITY,
  PARTICIPATION_CONTRIBUTION_MAX,
  PARTICIPATION_ROLE_MAX,
  PARTICIPATION_ROLE_SUGGESTIONS,
  participantLabel,
  type Board,
  type BoardParticipant,
  type BoardSnapshot,
  type CardParticipation,
} from '@kaba/shared';
import { api, ApiError } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { boardKey } from '../../lib/board';
import { toast } from '../../lib/toast';
import { inputCls } from '../AdminPage';
import { Section, SectionAction } from '../card/Section';
import { Button, cx, Sheet } from '../ui/ui';

/** Wunsch, die Board-Verwaltung der Beteiligten zu öffnen (Menü, Kartenabschnitt) */
export const openParticipants = (boardId: string) => window.dispatchEvent(new CustomEvent('kaba-participants-open', { detail: { boardId } }));

const online = () => {
  if (navigator.onLine) return true;
  toast.error('Beteiligte lassen sich nur mit Verbindung ändern.');
  return false;
};

function usePatch(boardId: string) {
  const qc = useQueryClient();
  return (fn: (s: BoardSnapshot) => Partial<BoardSnapshot>) => qc.setQueryData<BoardSnapshot>(boardKey(boardId), (s) => (s ? { ...s, ...fn(s) } : s));
}

const upsert = <T extends { id: string }>(list: T[] | undefined, x: T) => [...(list ?? []).filter((y) => y.id !== x.id), x];

/* ---------- Board: Verwaltung ---------- */

export function ParticipantsSheet({ open, onClose, snapshot }: { open: boolean; onClose: () => void; snapshot: BoardSnapshot }) {
  const me = useMe().data;
  const patch = usePatch(snapshot.board.id);
  const qc = useQueryClient();
  const boardId = snapshot.board.id;
  const enabled = !!snapshot.board.participantsEnabled;
  const isOwner = snapshot.board.role === 'owner' && !me?.isGuest;
  const canEdit = snapshot.board.role === 'owner' || snapshot.board.role === 'editor';
  const people = useMemo(() => [...(snapshot.participants ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'de')), [snapshot.participants]);
  const parts = snapshot.participations ?? [];
  const [name, setName] = useState('');
  const [org, setOrg] = useState('');
  const [editing, setEditing] = useState<BoardParticipant | null>(null);
  const [confirmDel, setConfirmDel] = useState<{ person: BoardParticipant; usage: { cardId: string; title: string; role: string; archived: boolean }[] } | null>(null);
  const [confirmOff, setConfirmOff] = useState<{ people: number; participations: number } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) {
      setEditing(null);
      setConfirmDel(null);
      setConfirmOff(null);
    }
  }, [open]);

  const toggle = async (on: boolean, confirm = false) => {
    if (!online()) return;
    setBusy(true);
    try {
      const b = await api.put<Board>(`/api/boards/${boardId}/participants/enabled`, { enabled: on, confirm });
      setConfirmOff(null);
      patch((s) => ({
        board: { ...s.board, participantsEnabled: b.participantsEnabled },
        ...(on ? { participants: s.participants ?? [], participations: s.participations ?? [] } : { participants: undefined, participations: undefined }),
      }));
      void qc.invalidateQueries({ queryKey: boardKey(boardId) });
      toast.info(on ? 'Beteiligte ohne Konto sind eingeschaltet.' : 'Beteiligte ohne Konto sind ausgeschaltet.');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const d = (e.data as { details?: { people: number; participations: number } } | undefined)?.details;
        setConfirmOff(d ?? { people: 0, participations: 0 });
      } else toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const add = async () => {
    if (!name.trim() || !online()) return;
    setBusy(true);
    try {
      const p = await api.post<BoardParticipant>(`/api/boards/${boardId}/participants`, { name, organization: org || null });
      patch((s) => ({ participants: upsert(s.participants, p) }));
      setName('');
      setOrg('');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const saveEdit = async () => {
    if (!editing || !online()) return;
    try {
      const p = await api.patch<BoardParticipant>(`/api/participants/${editing.id}`, { name: editing.name, organization: editing.organization || null, expectedVersion: editing.version });
      patch((s) => ({ participants: upsert(s.participants, p) }));
      setEditing(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const cur = (e.data as { details?: { current?: BoardParticipant } } | undefined)?.details?.current;
        if (cur) patch((s) => ({ participants: upsert(s.participants, cur) }));
        toast.error('Jemand hat diese Angaben inzwischen geändert. Bitte erneut prüfen.');
        setEditing(cur ?? null);
      } else toast.error((e as Error).message);
    }
  };
  const askDelete = async (person: BoardParticipant) => {
    if (!online()) return;
    try {
      const usage = await api.get<{ cardId: string; title: string; role: string; archived: boolean }[]>(`/api/participants/${person.id}/usage`);
      if (!usage.length) return void doDelete(person, false);
      setConfirmDel({ person, usage });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const doDelete = async (person: BoardParticipant, confirm: boolean) => {
    try {
      await api.del(`/api/participants/${person.id}${confirm ? '?confirm=1' : ''}`);
      patch((s) => ({ participants: (s.participants ?? []).filter((x) => x.id !== person.id), participations: (s.participations ?? []).filter((x) => x.participantId !== person.id) }));
      setConfirmDel(null);
      toast.info(`„${person.name}“ gelöscht.`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) void askDelete(person);
      else toast.error((e as Error).message);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Beteiligte ohne Konto">
      <div className="flex flex-col gap-4 p-4">
        <p className="text-sm text-[var(--ink-soft)]">
          Menschen, die etwas beitragen, ohne in Kaba mitzuarbeiten – etwa „liefert: Druckdaten bis Freitag“. Nur Name oder Bezeichnung und Organisation, keine Kontaktdaten. Zuständig bleiben die
          Mitglieder unter „Zuständig“.
        </p>
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200" role="note">
          {PARTICIPANTS_VISIBILITY}
        </p>
        {!enabled &&
          (isOwner ? (
            <Button type="button" variant="primary" className="self-start" disabled={busy} onClick={() => void toggle(true)}>
              Für dieses Board einschalten
            </Button>
          ) : (
            <p className="text-sm">Für dieses Board nicht eingeschaltet. Das können Besitzer*innen des Boards tun.</p>
          ))}
        {enabled && (
          <>
            {canEdit && (
              <form
                className="flex flex-col gap-2 rounded-lg bg-[var(--column)] p-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  void add();
                }}
              >
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium">Name oder Bezeichnung</span>
                  <input className={inputCls} value={name} maxLength={PARTICIPANT_NAME_MAX} onChange={(e) => setName(e.target.value)} placeholder="z. B. Hausmeister, Frau Petersen" />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  <span className="font-medium">Organisation (optional)</span>
                  <input className={inputCls} value={org} maxLength={PARTICIPANT_ORG_MAX} onChange={(e) => setOrg(e.target.value)} placeholder="z. B. Druckerei Nord" />
                </label>
                <Button type="submit" className="self-start" disabled={busy || !name.trim()}>
                  <UserPlus size={15} aria-hidden /> Beteiligte hinzufügen
                </Button>
              </form>
            )}
            {!people.length && <p className="text-sm text-[var(--ink-soft)]">Noch keine Beteiligten. Offene Rollenplätze an Karten lassen sich später einer Person zuordnen.</p>}
            <ul className="flex flex-col gap-1.5">
              {people.map((p) => {
                const n = parts.filter((x) => x.participantId === p.id).length;
                if (editing?.id === p.id)
                  return (
                    <li key={p.id} className="flex flex-col gap-2 rounded-lg border border-[var(--line)] p-2.5">
                      <input
                        aria-label="Name oder Bezeichnung"
                        className={inputCls}
                        value={editing.name}
                        maxLength={PARTICIPANT_NAME_MAX}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      />
                      <input
                        aria-label="Organisation"
                        className={inputCls}
                        value={editing.organization ?? ''}
                        maxLength={PARTICIPANT_ORG_MAX}
                        onChange={(e) => setEditing({ ...editing, organization: e.target.value })}
                      />
                      <div className="flex gap-2">
                        <Button type="button" disabled={!editing.name.trim()} onClick={() => void saveEdit()}>
                          Speichern
                        </Button>
                        <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
                          Abbrechen
                        </Button>
                      </div>
                    </li>
                  );
                return (
                  <li key={p.id} className="flex min-h-11 items-center gap-2 rounded-lg border border-[var(--line)] px-2.5 text-sm">
                    <Contact size={16} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{p.name}</span>
                      <span className="block truncate text-xs text-[var(--ink-soft)]">
                        {[p.organization, n === 0 ? 'an keiner Karte' : n === 1 ? 'an 1 Karte' : `an ${n} Karten`].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          aria-label={`„${p.name}“ bearbeiten`}
                          className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10"
                          onClick={() => setEditing(p)}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          aria-label={`„${p.name}“ löschen`}
                          className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:text-rose-600"
                          onClick={() => void askDelete(p)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
            {confirmDel && (
              <div role="alertdialog" aria-label={`„${confirmDel.person.name}“ löschen`} className="flex flex-col gap-2 rounded-lg border border-rose-300 p-3 text-sm dark:border-rose-500/40">
                <p className="font-medium">„{confirmDel.person.name}“ löschen? Diese Beteiligungen werden entfernt:</p>
                <ul className="list-disc pl-5">
                  {confirmDel.usage.map((u, i) => (
                    <li key={i}>
                      {u.role} – {u.archived ? `${u.title} (archiviert)` : u.title}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2">
                  <Button type="button" variant="danger" onClick={() => void doDelete(confirmDel.person, true)}>
                    Person und Beteiligungen löschen
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setConfirmDel(null)}>
                    Abbrechen
                  </Button>
                </div>
              </div>
            )}
            {isOwner && (
              <div className="border-t border-[var(--line)] pt-3">
                {confirmOff ? (
                  <div role="alertdialog" aria-label="Beteiligte ausschalten" className="flex flex-col gap-2 text-sm">
                    <p>
                      Beim Ausschalten werden {confirmOff.people === 1 ? '1 Beteiligte*r' : `${confirmOff.people} Beteiligte`} und{' '}
                      {confirmOff.participations === 1 ? '1 Beteiligung' : `${confirmOff.participations} Beteiligungen`} an Karten gelöscht.
                    </p>
                    <div className="flex gap-2">
                      <Button type="button" variant="danger" disabled={busy} onClick={() => void toggle(false, true)}>
                        Ausschalten und löschen
                      </Button>
                      <Button type="button" variant="ghost" onClick={() => setConfirmOff(null)}>
                        Abbrechen
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button type="button" variant="ghost" disabled={busy} onClick={() => void toggle(false)}>
                    Für dieses Board ausschalten
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Sheet>
  );
}

/* ---------- Karte: Abschnitt „Beteiligte“ ---------- */

type Draft = { id: string | null; version: number; participantId: string | null; role: string; contribution: string; newName: string; newOrg: string };
const NEW = '__neu__';

export function CardParticipants({ id, cardId, snapshot, canEdit }: { id?: string; cardId: string; snapshot: BoardSnapshot; canEdit: boolean }) {
  const patch = usePatch(snapshot.board.id);
  const boardId = snapshot.board.id;
  const people = useMemo(() => [...(snapshot.participants ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'de')), [snapshot.participants]);
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const items = useMemo(() => (snapshot.participations ?? []).filter((x) => x.cardId === cardId), [snapshot.participations, cardId]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const listId = `rollen-${cardId}`;
  // Escape im Formular schließt nur das Formular (die Kartendetails melden es über „kaba-escape“)
  const escRef = useCallback((el: HTMLFormElement | null) => el?.addEventListener('kaba-escape', () => setDraft(null)), []);
  if (!snapshot.board.participantsEnabled) return null;
  if (!items.length && !canEdit) return null;

  const save = async () => {
    if (!draft || !draft.role.trim() || !online()) return;
    setBusy(true);
    try {
      let participantId = draft.participantId === NEW ? null : draft.participantId;
      if (draft.participantId === NEW) {
        if (!draft.newName.trim()) return void toast.error('Bitte einen Namen oder eine Bezeichnung angeben.');
        const p = await api.post<BoardParticipant>(`/api/boards/${boardId}/participants`, { name: draft.newName, organization: draft.newOrg || null });
        patch((s) => ({ participants: upsert(s.participants, p) }));
        participantId = p.id;
      }
      const body = { participantId, role: draft.role, contribution: draft.contribution || null };
      const r = draft.id
        ? await api.patch<CardParticipation>(`/api/participations/${draft.id}`, { ...body, expectedVersion: draft.version })
        : await api.post<CardParticipation>(`/api/cards/${cardId}/participations`, body);
      patch((s) => ({ participations: upsert(s.participations, r) }));
      setDraft(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const cur = (e.data as { details?: { current?: CardParticipation } } | undefined)?.details?.current;
        if (cur) patch((s) => ({ participations: upsert(s.participations, cur) }));
        toast.error('Jemand hat diese Beteiligung inzwischen geändert. Bitte erneut prüfen.');
        setDraft(null);
      } else toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async (p: CardParticipation) => {
    if (!online()) return;
    try {
      await api.del(`/api/participations/${p.id}`);
      patch((s) => ({ participations: (s.participations ?? []).filter((x) => x.id !== p.id) }));
      toast.info('Beteiligung entfernt – die Person bleibt im Board.');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };
  const edit = (p: CardParticipation | null, assign = false) =>
    setDraft(
      p
        ? { id: p.id, version: p.version, participantId: p.participantId ?? (assign ? (people[0]?.id ?? NEW) : null), role: p.role, contribution: p.contribution ?? '', newName: '', newOrg: '' }
        : { id: null, version: 0, participantId: people[0]?.id ?? NEW, role: '', contribution: '', newName: '', newOrg: '' },
    );

  return (
    <Section
      id={id}
      title="Beteiligte"
      aside={
        canEdit && !draft ? (
          <SectionAction onClick={() => edit(null)}>
            <Plus size={15} aria-hidden /> Beteiligung …
          </SectionAction>
        ) : undefined
      }
    >
      {!items.length && !draft && <p className="text-sm text-[var(--ink-soft)]">Niemand ohne Konto beteiligt. Zuständig bleiben die Mitglieder unter „Zuständig“.</p>}
      {!!items.length && (
        <ul className="flex flex-col gap-1.5">
          {items.map((p) => {
            const person = p.participantId ? byId.get(p.participantId) : undefined;
            return (
              <li key={p.id} className="flex min-h-11 items-center gap-2 rounded-lg border border-[var(--line)] px-2.5 py-1 text-sm">
                <span className="shrink-0 rounded bg-black/5 px-1.5 py-0.5 text-xs font-medium dark:bg-white/10">{p.role}</span>
                <span className="min-w-0 flex-1">
                  {person ? <span className="block truncate">{participantLabel(person)}</span> : <span className="block truncate text-[var(--ink-soft)] italic">Offen</span>}
                  {p.contribution && <span className="block truncate text-xs text-[var(--ink-soft)]">{p.contribution}</span>}
                </span>
                {canEdit && !person && (
                  <Button type="button" variant="ghost" onClick={() => edit(p, true)}>
                    Person zuordnen
                  </Button>
                )}
                {canEdit && (
                  <>
                    <button
                      type="button"
                      aria-label={`Beteiligung „${p.role}“ bearbeiten`}
                      className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:bg-black/5 dark:hover:bg-white/10"
                      onClick={() => edit(p)}
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Beteiligung „${p.role}“ entfernen`}
                      className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-[var(--ink-soft)] hover:text-rose-600"
                      onClick={() => void remove(p)}
                    >
                      <X size={15} />
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {draft && (
        <form
          className="flex flex-col gap-2 rounded-lg bg-[var(--column)] p-2"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
          data-keep-escape
          ref={escRef}
        >
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Rolle</span>
            <input
              autoFocus
              list={listId}
              className={inputCls}
              value={draft.role}
              maxLength={PARTICIPATION_ROLE_MAX}
              placeholder="z. B. liefert, prüft, Ansprechperson"
              onChange={(e) => setDraft({ ...draft, role: e.target.value })}
            />
            <datalist id={listId}>
              {PARTICIPATION_ROLE_SUGGESTIONS.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Person</span>
            <select className={inputCls} value={draft.participantId ?? ''} onChange={(e) => setDraft({ ...draft, participantId: e.target.value || null })}>
              <option value="">Offen (noch niemand)</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {participantLabel(p)}
                </option>
              ))}
              <option value={NEW}>Neue Person …</option>
            </select>
          </label>
          {draft.participantId === NEW && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                aria-label="Name oder Bezeichnung"
                className={inputCls}
                value={draft.newName}
                maxLength={PARTICIPANT_NAME_MAX}
                placeholder="Name oder Bezeichnung"
                onChange={(e) => setDraft({ ...draft, newName: e.target.value })}
              />
              <input
                aria-label="Organisation (optional)"
                className={inputCls}
                value={draft.newOrg}
                maxLength={PARTICIPANT_ORG_MAX}
                placeholder="Organisation (optional)"
                onChange={(e) => setDraft({ ...draft, newOrg: e.target.value })}
              />
            </div>
          )}
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Beitrag (optional)</span>
            <input
              className={inputCls}
              value={draft.contribution}
              maxLength={PARTICIPATION_CONTRIBUTION_MAX}
              placeholder="z. B. Druckdaten bis Freitag"
              onChange={(e) => setDraft({ ...draft, contribution: e.target.value })}
            />
          </label>
          <p className="text-xs text-[var(--ink-soft)]">{PARTICIPANTS_VISIBILITY} Keine Kontaktdaten eintragen.</p>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={busy || !draft.role.trim()}>
              Speichern
            </Button>
            <Button type="button" variant="ghost" onClick={() => setDraft(null)}>
              Abbrechen
            </Button>
            <Button type="button" variant="ghost" className={cx('ml-auto')} onClick={() => openParticipants(boardId)}>
              Beteiligte verwalten …
            </Button>
          </div>
        </form>
      )}
    </Section>
  );
}

/** Für den Arbeitsstand: Anzahl Beteiligungen an dieser Karte (nur wenn eingeschaltet) */
export function useCardParticipationCount(snapshot: BoardSnapshot, cardId: string) {
  return useMemo(
    () => (snapshot.board.participantsEnabled ? (snapshot.participations ?? []).filter((x) => x.cardId === cardId) : []),
    [snapshot.board.participantsEnabled, snapshot.participations, cardId],
  );
}
